import React, { useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { paymentsApi } from "./paymentsApi";
import { toast } from "react-toastify";
import ContributionCelebrationModal from "./bonus/ContributionCelebrationModal";
import TargetProgressModal from "./target/TargetProgressModal";

const PAYMENT_METHODS = ["Bank Transfer", "Cash", "Card", "Cheque", "Other"];

function buildIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `pay-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isLikelyNetworkError(error) {
  const msg = String(error?.message || "").toLowerCase();
  return msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("network error");
}

export default function RecordPaymentModal({
  show,
  onHide,
  stageId,
  enquiryId,
  planId,
  stageNumber,
  maxAmount,
  requireReceipt,
  onSuccess,
}) {
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0]);
  const [notes, setNotes] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPath, setReceiptPath] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(() => buildIdempotencyKey());
  const [bonusContributions, setBonusContributions] = useState([]);
  const [targetContributions, setTargetContributions] = useState([]);

  const reset = () => {
    setAmount("");
    setPaymentMethod(PAYMENT_METHODS[0]);
    setNotes("");
    setReceiptFile(null);
    setReceiptPath("");
    setError("");
    setIdempotencyKey(buildIdempotencyKey());
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setReceiptFile(f || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setError("Enter a valid amount.");
      return;
    }
    if (requireReceipt && !receiptPath && !receiptFile) {
      setError("Receipt upload is required.");
      return;
    }
    setSaving(true);
    try {
      let receipt = receiptPath;
      if (receiptFile) {
        const up = await paymentsApi.uploadReceipt(receiptFile);
        receipt = up?.receipt_file || receipt;
      }
      const data = await paymentsApi.recordTransaction({
        stage_id: stageId,
        amount: amt,
        payment_method: paymentMethod,
        receipt_file: receipt || undefined,
        notes: notes.trim() || undefined,
      }, { idempotencyKey });
      toast.success("Payment recorded.");
      reset();
      onHide();
      onSuccess?.();
      if (data?.bonus_contributions?.length) {
        setBonusContributions(data.bonus_contributions);
      }
      if (data?.target_contributions?.length) {
        setTargetContributions(data.target_contributions);
      }
    } catch (err) {
      if (isLikelyNetworkError(err)) {
        try {
          if (planId) {
            await paymentsApi.listStages(planId);
          }
          await onSuccess?.();
        } catch (_) {
          // Best effort refresh only; preserve original error context below.
        }
        setError(
          "Network issue while saving payment. Latest stage data was refreshed. Please verify stage balance before retrying."
        );
      } else {
        setError(err.message || "Failed to record payment.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Modal show={show} onHide={handleClose} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>Record Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {stageNumber != null && (
          <p className="text-muted small mb-2">Stage {stageNumber}</p>
        )}
        <Form onSubmit={handleSubmit}>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Amount *</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              max={maxAmount ?? undefined}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={maxAmount != null ? `Max ${maxAmount}` : ""}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Payment Method *</Form.Label>
            <Form.Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Receipt {requireReceipt ? "*" : "(optional)"}</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
            {receiptPath && !receiptFile && (
              <Form.Text className="text-muted">Stored: {receiptPath}</Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
            />
          </Form.Group>
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Record Payment"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
    <ContributionCelebrationModal
      open={bonusContributions.length > 0}
      contributions={bonusContributions}
      onClose={() => setBonusContributions([])}
    />
    <TargetProgressModal
      open={targetContributions.length > 0}
      contributions={targetContributions}
      onClose={() => setTargetContributions([])}
    />
    </>
  );
}
